import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import useComprehensiveTopNav from 'hooks/useComprehensiveTopNav';
import { useWindowResize } from 'hooks/useWindowResize';
import { Typography, Image, LoadingIndicator } from 'lib/components';
import { DatePicker } from 'lib/components/FormFields';
import {
	useAppDispatch,
	actions,
	useSelectRequest,
	useSelectHospitals,
	useSelectHospital,
	RequestManager,
} from 'store';
import routes from 'lib/api/routes';
import useCustomToast from 'hooks/toast';
import { Timing } from 'lib/util';
import classes from './index.module.scss';

/**
 * Props interface for the EmployeeCreateScreenView component
 */
interface Props {
	/** ID of the hospital where the employee will be created */
	hospitalId: number;
}

/**
 * EmployeeCreateScreen component
 * Wrapper component that validates the hospital ID from URL params
 * Fetches hospital data and renders the form view when valid
 *
 * @returns {JSX.Element} - Rendered component
 */
const EmployeeCreateScreen = () => {
	// Get hospital ID from URL parameters
	const { hospitalId } = useParams<{ hospitalId: string }>();

	// Redux state and actions
	const dispatch = useAppDispatch();
	// Hospital data from Redux store
	const hospitals = useSelectHospitals();
	// Selector function to get a hospital by ID
	const getHospitalById = useSelectHospital();

	// Track whether the hospital ID is valid
	const [isValid, setIsValid] = useState(false);

	/**
	 * Fetch hospitals data on component mount
	 */
	useEffect(() => {
		dispatch(actions.fetchHospitals());
	}, []);

	/**
	 * Validate hospital ID when hospitals data is updated
	 * Sets isValid to true if the hospital exists
	 */
	useEffect(() => {
		const hospital = getHospitalById(parseInt(hospitalId));

		if (hospital) {
			setIsValid(true);
			return;
		}
	}, [hospitals.updatedAt]);

	// Render form view if hospital ID is valid, otherwise show loading indicator
	return isValid ? <EmployeeCreateScreenView hospitalId={parseInt(hospitalId)} /> : <LoadingIndicator />;
};

/**
 * EmployeeCreateScreenView component
 * Form for creating a new hospital employee
 * Handles form state and submission
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element} - Rendered component
 */
const EmployeeCreateScreenView = ({ hospitalId }: Props) => {
	// Custom toast notifications for user feedback
	const toast = useCustomToast();
	// Navigation helper
	const { push } = useHistory();
	// Responsive design helpers
	const { isSmallDevice, isSmallerDevice } = useWindowResize();

	// Redux state and actions
	const dispatch = useAppDispatch();
	// Active API request tracking
	const { active } = useSelectRequest();

	// Form state variables
	/** Employee name input value */
	const [name, setName] = useState('');
	/** Employee email input value */
	const [email, setEmail] = useState('');
	/** Employee phone number input value */
	const [phoneNumber, setPhoneNumber] = useState('');
	/** Employee password input value */
	const [password, setPassword] = useState('');
	/** Employee date of birth - defaults to current date */
	const [dateOfBirth, setDateOfBirth] = useState<Date>(new Date());

	/**
	 * Monitor API requests for employee creation
	 * Shows toast notifications for success/failure and redirects on success
	 */
	useEffect(() => {
		const RM = new RequestManager(active);
		if (RM.idle()) return;

		if (RM.req(routes.HOSPITAL_EMPLOYEE(hospitalId), 'POST')) {
			if (RM.failed()) {
				toast(active.message, 'error');
			} else if (RM.succeeded()) {
				toast(active.message);
				// Redirect to hospital employees list on success
				push(`/hospitals/${hospitalId}/employees`);
			}
		}
	}, [active]);

	// Set up the top navigation bar with title
	useComprehensiveTopNav('New Hospital Employee');

	/**
	 * Handle form submission to create a new employee
	 * Dispatches the addEmployee action with form data
	 */
	const handleCreate = () => {
		dispatch(
			actions.addEmployee(hospitalId, {
				name,
				email,
				phoneNumber,
				// Format date to string format expected by the API
				dateOfBirth: Timing.formatDate(dateOfBirth),
				password,
			})
		);
	};

	return (
		<div
			className={classes['profile-container']}
			style={{
				flexDirection: 'column',
			}}
		>
			<>
				{/* Profile header section with avatar and name */}
				<div className={classes['top-container']}>
					{/* Avatar image area */}
					<div className={classes['image-area']}>
						<div
							className={classes['profile-image-container']}
							style={{
								margin: '2rem',
								border: 'none',
							}}
						>
							{/* Default placeholder avatar */}
							<Image src={'plAvatar'} style={{ width: '100%', height: '100%' }} />
						</div>
					</div>
					{/* Basic profile details */}
					<div className={classes['profile-details']}>
						{/* Name input field */}
						<div style={{ margin: '0rem 1rem 1rem 0' }}>
							<Typography color="darkgrey" variant={isSmallerDevice ? 'h5' : 'subheading2'}>
								Name:
							</Typography>
							<input
								className={classes['input']}
								style={{
									width: '15rem',
									minWidth: 250,
								}}
								value={name}
								onChange={(e) => setName(e.target.value)}
							/>
						</div>
					</div>
				</div>
				{/* Contact information section */}
				<div
					className={classes['bottom-container']}
					style={{
						padding: isSmallDevice ? '1rem' : undefined,
					}}
				>
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
						}}
					>
						<Typography weight="bold">Basic info:</Typography>
						{/* Contact information row */}
						<div className={classes['row']}>
							{/* Phone number input */}
							<div style={{ margin: '1rem 1rem 1rem 0' }}>
								<Typography color="darkgrey" variant={isSmallerDevice ? 'h5' : 'subheading2'}>
									Mobile number:
								</Typography>
								<input
									className={`${classes['input']} ${classes['palewhite']}`}
									style={{
										width: '15rem',
									}}
									value={phoneNumber}
									onChange={(e) => setPhoneNumber(e.target.value)}
								/>
							</div>
							{/* Email input */}
							<div style={{ margin: '1rem 1rem 1rem 0' }}>
								<Typography color="darkgrey" variant={isSmallerDevice ? 'h5' : 'subheading2'}>
									Email:
								</Typography>
								<input
									className={`${classes['input']} ${classes['palewhite']}`}
									style={{
										width: '15rem',
										minWidth: 180,
									}}
									value={email}
									onChange={(e) => setEmail(e.target.value)}
								/>
							</div>
							{/* Date of birth picker */}
							<div style={{ margin: '1rem 1rem 1rem 0' }}>
								<DatePicker label="Date of Birth" value={dateOfBirth} setValue={setDateOfBirth} />
							</div>
						</div>
						{/* Password row */}
						<div className={classes['row']} style={{ marginTop: '1.5rem' }}>
							{/* Password input */}
							<div style={{ margin: '0rem 1rem 1rem 0' }}>
								<Typography color="darkgrey" variant={isSmallerDevice ? 'h5' : 'subheading2'}>
									Password:
								</Typography>
								<input
									className={`${classes['input']} ${classes['palewhite']}`}
									style={{
										width: '15rem',
										minWidth: 200,
									}}
									placeholder="***********"
									type="password"
									value={password}
									onChange={(e) => setPassword(e.target.value)}
								/>
							</div>
							{/* Password confirmation input - currently disabled
							<div style={{ margin: '0rem 1rem 1rem 0' }}>
								<Typography color="darkgrey" variant={isSmallerDevice ? 'h5' : 'subheading2'}>
									Confirm Password:
								</Typography>
								<input
									className={`${classes['input']} ${classes['palewhite']}`}
									style={{
										width: '15rem',
										minWidth: 200,
									}}
									placeholder="***********"
									type="password"
									value={newPasswordConfirmation}
									onChange={(e) => setNewPasswordConfirmation(e.target.value)}
								/>
							</div> */}
						</div>
					</div>
				</div>
				{/* Form action buttons */}
				<div
					className={classes['row']}
					style={{
						margin: 'auto 0 1rem 1rem',
					}}
				>
					{/* Create employee button */}
					<button
						className={classes['button']}
						style={{
							width: 'auto',
							marginRight: '1rem',
						}}
						onClick={handleCreate}
					>
						<p>Add Employee</p>
					</button>
					{/* Cancel button - returns to hospital employees list */}
					<button
						className={`${classes['button']} ${classes['palewhite']}`}
						style={{
							width: 'auto',
						}}
						onClick={() => push(`/hospitals/${hospitalId}/employees`)}
					>
						<p>Cancel</p>
					</button>
				</div>
			</>
		</div>
	);
};

export default EmployeeCreateScreen;
